import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Subject, takeUntil } from 'rxjs';
import { BUSINESS_TYPE } from 'src/app/constants/endpoint.const';
import { SlaBusinessInformation } from 'src/app/models/documents.model';

import { BaseService } from 'src/app/services/base.service';

import { FormValidationService } from 'src/app/services/form-validation.service';
import { BusinessTypeListResponseType } from 'src/app/types/response/document-state.response.type';
import { setRegex } from 'src/app/libs/functions/common.functions';

@Component({
  selector: 'app-business-information-input',
  standalone: false,
  templateUrl: './business-information-input.component.html',
  styleUrl: './business-information-input.component.scss',
})
export class BusinessInformationInputComponent implements OnInit, OnDestroy {
  destroy$ = new Subject();
  setRegex = setRegex;
  selectedType!: number;
  businessTypeList: BusinessTypeListResponseType[] = [];

  @Input() slaDataInput: SlaBusinessInformation = {
    business_entity: 1,
    business_name: '',
    business_email: '',
    business_type: 1,
    business_address: '',
    business_phone: '',
  };
  @Output() businessData = new EventEmitter<SlaBusinessInformation>();
  businessForm!: FormGroup;
  radioValue: number = 0;
  current: number = 1;
  errMessEmail: string = '';
  validEmail: boolean = true;
  errMessBName: string = '';
  validBName: boolean = true;
  validNumber: boolean = true;
  errMessNumber: string = '';
  validAddress: boolean = true;
  errMessAddress: string = '';

  validEntityOthers: boolean = true;
  errMessEntityOthers: string = '';
  isConfirmBack: boolean = false
  @Output() openModalConfirmBack = new EventEmitter<boolean>();
  serviceType: string = localStorage.getItem('service_type_doc') || '';
  mode: string = localStorage.getItem('mode-sla-input') || '';

  constructor(
    private BaseService: BaseService,
    private fb: FormBuilder,
    private router: Router,
    private formValidation: FormValidationService,
    private message: NzMessageService
  ) { }

  ngOnInit() {
    this.initForm();
    this.setupFormSubscriptions();
    this.getBusinessType();

    if (this.businessForm && this.slaDataInput) {
      this.businessForm.patchValue(this.slaDataInput);
      this.checkEntityOthersState();
    }
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  setupFormSubscriptions() {
    this.businessForm.get('business_entity')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      const othersControl = this.businessForm.get('business_entity_others_name');
      if (value === 5) {
        othersControl?.setValidators([Validators.required, Validators.maxLength(20)]);
      } else {
        othersControl?.clearValidators();
        othersControl?.setValue('', { emitEvent: false });
        this.validEntityOthers = true;
        this.errMessEntityOthers = '';
      }
      othersControl?.updateValueAndValidity({ emitEvent: false });
      this.validateEntityOthers();
    });

    this.businessForm.get('business_entity_others_name')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.validateEntityOthers();
    });
  }

  checkEntityOthersState() {
    const entityValue = this.businessForm.get('business_entity')?.value;
    const othersControl = this.businessForm.get('business_entity_others_name');

    if (entityValue === 5) {
      othersControl?.setValidators([Validators.required, Validators.maxLength(20)]);
      othersControl?.updateValueAndValidity({ emitEvent: false });
      this.validateEntityOthers();
    } else {
      othersControl?.clearValidators();
      othersControl?.updateValueAndValidity({ emitEvent: false });
      this.validEntityOthers = true;
      this.errMessEntityOthers = '';
    }
  }



  ngOnChanges(changes: SimpleChanges): void {
    if (changes['slaDataInput'] && changes['slaDataInput'].currentValue) {
      const slaData = changes['slaDataInput'].currentValue;

      if (this.businessForm) {
        this.businessForm.patchValue(slaData);
        this.checkEntityOthersState();
      }
    }
  }

  initForm() {
    this.businessForm = this.fb.group({
      business_entity: [null, Validators.required],
      business_entity_others_name: [''],
      business_name: ['', Validators.required],
      business_email: ['', [Validators.required, Validators.email]],
      business_type: [null, Validators.required],
      business_address: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(100)]],
      business_phone: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
    });
  }

  back(): void {
    this.isConfirmBack = true
  }

  closeConfirmBack() {
    this.isConfirmBack = false
  }

  getBusinessType() {
    this.BaseService.getData(BUSINESS_TYPE)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: { data: BusinessTypeListResponseType[] }) => {
          this.businessTypeList = res.data;
        },
        (err) => {
          this.message.error(err.error.meta.message || 'Failed to get business type');
        }
      );
  }

  onSubmit(): void {
    if (this.businessForm.valid) {
      if (this.current < 3) {
        this.current += 1;
        this.businessData.emit(this.businessForm.value);
        if (this.mode === 'create') {
          this.router.navigate(['/document/sla-data-input'], {
            queryParams: { step: 2, service_type: this.serviceType },
          });
        } else {
          this.router.navigate(['/document/sla-data-input'], {
            queryParams: { step: 2, mode: this.mode, service_type: this.serviceType },
          });
        }
      }
    } else {
      this.message.error('Form is invalid!');
    }
  }
  previus(): void {
    if (this.current > 0) {
      this.current -= 1;
      if (this.mode === 'create') {
        this.router.navigate(['/document/sla-data-input'], {
          queryParams: { step: this.current + 1, service_type: this.serviceType },
        });
      } else {
        this.router.navigate(['/document/sla-data-input'], {
          queryParams: { step: this.current + 1, mode: this.mode, service_type: this.serviceType },
        });
      }
    }
  }

  validateEntityOthers() {
    if (this.businessForm.get('business_entity')?.value !== 5) return;

    const value = this.businessForm.get('business_entity_others_name')?.value || '';
    if (value === '') {
      this.validEntityOthers = false;
      this.errMessEntityOthers = 'Please fill in this field';
    } else if (value.length > 20) {
      this.validEntityOthers = false;
      this.errMessEntityOthers = 'Maximum 20 characters';
    } else {
      this.validEntityOthers = true;
      this.errMessEntityOthers = '';
    }
  }

  setRegexBName(event: KeyboardEvent) {
    const regex = /^[a-zA-Z .-]+$/g;
    return regex.test(event.key);
  }

  validateBusinessName() {
    const value = this.businessForm.get('business_name')?.value || '';

    if (value === '') {
      this.validBName = false
      this.errMessBName = 'Business Name must be filled in';
    } else if (value.length < 3) {
      this.validBName = false;
      this.errMessBName = 'Minimum 3 characters';
    } else if (value.length > 50) {
      this.validBName = false;
      this.errMessBName = 'Maximum 50 characters';
    } else {
      this.validBName = true;
      this.errMessBName = '';
    }
  }

  validateEmail() {
    const value = this.businessForm.get('business_email')?.value || '';

    const result = this.formValidation.validateEmail(value)
    this.validEmail = result.valid;
    this.errMessEmail = result.message;
  }



  preventNonNumeric(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;

    if (
      !/^\d$/.test(event.key) &&
      !['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab'].includes(
        event.key
      )
    ) {
      event.preventDefault();
    }

    if (input.value.length === 0 && event.key !== '8') {
      event.preventDefault();
    }
  }

  validateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const result = this.formValidation.validatePhoneNumber(input.value)
    this.validNumber = result.valid;
    this.errMessNumber = result.message;
  }

  validateAddress() {
    const value = this.businessForm.get('business_address')?.value || '';

    if (value.length === 0) {
      this.validAddress = false;
      this.errMessAddress = 'Address cannot be empty';
    } else if (value.length > 0 && value.length < 10) {
      this.validAddress = false;
      this.errMessAddress = 'Minimum 10 characters';
    } else if (value.length > 100) {
      this.validAddress = false;
      this.errMessAddress = 'Maximum 100 characters';
    } else {
      this.validAddress = true;
      this.errMessAddress = '';
    }
  }

  get isFormInvalid(): boolean {
    return (
      this.businessForm.invalid ||
      this.errMessEmail !== '' ||
      this.errMessBName !== '' ||
      this.errMessAddress !== '' ||
      this.errMessNumber !== '' ||
      this.errMessEntityOthers !== ''
    );
  }
}
